import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const AttributeTerms = () => {
    const { attributeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSuccess, showError } = useToast();
    
    const [attribute, setAttribute] = useState(location.state?.attribute || null);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTerm, setEditingTerm] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
    });

    useEffect(() => {
        if (!attribute) {
            loadAttribute();
        }
        loadTerms();
    }, [attributeId]);

    const loadAttribute = async () => {
        try {
            const response = await api.getAttribute(attributeId);
            if (response.success) {
                setAttribute(response.attribute);
            }
        } catch (error) {
            showError('Failed to load attribute');
        }
    };

    const loadTerms = async () => {
        try {
            setLoading(true);
            const response = await api.getAttributeTerms(attributeId);
            if (response.success) {
                setTerms(response.terms || []);
            }
        } catch (error) {
            showError('Failed to load terms');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingTerm) {
                const response = await api.updateAttributeTerm(attributeId, editingTerm.id, formData);
                if (response.success) {
                    showSuccess('Term updated successfully');
                    loadTerms();
                    resetForm();
                }
            } else {
                const response = await api.createAttributeTerm(attributeId, formData);
                if (response.success) {
                    showSuccess('Term created successfully');
                    loadTerms();
                    resetForm();
                }
            }
        } catch (error) {
            showError(error.message || 'Failed to save term');
        }
    };

    const handleEdit = (term) => {
        setEditingTerm(term);
        setFormData({
            name: term.name,
            slug: term.slug,
            description: term.description || '',
        });
        setShowForm(true);
    };

    const handleDelete = async (termId) => {
        if (!confirm('Are you sure you want to delete this term?')) return;

        try {
            const response = await api.deleteAttributeTerm(attributeId, termId);
            if (response.success) {
                showSuccess('Term deleted successfully');
                loadTerms();
            }
        } catch (error) {
            showError('Failed to delete term');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
        });
        setEditingTerm(null);
        setShowForm(false);
    };

    return (
        <div className="page attribute-terms-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => navigate(-1)}
                            style={{ marginRight: '12px' }}
                        >
                            ← Back
                        </button>
                        <div>
                            <h1>Configure Terms: {attribute?.name || 'Loading...'}</h1>
                            <p>Manage terms for this attribute</p>
                        </div>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? 'Cancel' : '+ Add Term'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="taxonomy-form-section">
                        <h3>{editingTerm ? 'Edit Term' : 'Add New Term'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        placeholder="e.g., Red, Large, Cotton"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                        placeholder="Auto-generated from name"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                    placeholder="Optional term description"
                                />
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    {editingTerm ? 'Update' : 'Create'} Term
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="taxonomy-list">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingState message="Loading terms..." />
                        </div>
                    ) : terms.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🏷️</div>
                            <h3>No terms yet</h3>
                            <p>Create your first term to get started</p>
                        </div>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Slug</th>
                                    <th>Description</th>
                                    <th>Count</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {terms.map(term => (
                                    <tr key={term.id}>
                                        <td><strong>{term.name}</strong></td>
                                        <td>{term.slug}</td>
                                        <td>{term.description || '-'}</td>
                                        <td>{term.count || 0}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleEdit(term)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDelete(term.id)}
                                                style={{marginLeft: '8px'}}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttributeTerms;
